import React, { useState, useRef, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, X, LayoutDashboard, Home, FileText, 
  Layers, ClipboardList, Users, ShieldCheck, ChevronDown, 
  LogOut,
  Printer,
} from 'lucide-react';
import { Drawer, IconButton, useMediaQuery } from '@mui/material';
import '../css/dashboard/Sidebar.css';
import { AuthContext } from '../../context/authContext';
import { Datacontext } from '../../context/dataContext';

const Sidebar = ({ user }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const location = useLocation();
  const menuRefs = useRef({});
  const { logout } = useContext(AuthContext);
  const { marquee } = useContext(Datacontext);

  // Detect screens smaller than 992px
  const isMobile = useMediaQuery('(max-width:992px)');

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setOpenDropdown(null);
  };

  const handleMobileToggle = () => setMobileOpen(!mobileOpen);

  const TitleCase = (str) => {
    return str ? str.toLowerCase().trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : "User";
  };

  const handleDropdown = (name) => {
    if (isCollapsed && !isMobile) return; 
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const isActive = (path) => location.pathname === path ? 'active1' : '';

  // --- INTERNAL MENU CONTENT (Your original logic) ---
  const renderMenuContent = () => (
    <div className={`menu-wrapper1 ${isMobile ? 'mobile-mode' : ''}`}>
      <ul className="main-menu1">
        {/* Dashboard */}
        <li className="menu-item1">
          <Link to="/dashboard" className={`menu-link1 ${isActive('/dashboard')}`} onClick={() => isMobile && setMobileOpen(false)}>
            <LayoutDashboard size={22} className="link-icon1" />
            <span className="link-text1">Dashboard</span>
          </Link>
        </li>

        {/* Processing */}
        <li className="menu-item1">
          <Link to="/home" className={`menu-link1 ${isActive('/home')}`} onClick={() => isMobile && setMobileOpen(false)}>
            <Home size={22} className="link-icon1" />
            <span className="link-text1">Processing</span>
          </Link>
        </li>

        {/* Document Dropdown */}
        <li className={`menu-item1 has-dropdown1 ${openDropdown === 'doc' ? 'is-open1' : ''}`}>
          <div className="menu-link1" onClick={() => handleDropdown('doc')}>
            <FileText size={22} className="link-icon1" />
            <span className="link-text1">Document</span>
            <ChevronDown size={16} className="arrow-icon1" />
          </div>
          <ul 
            className="sub-menu1"
            ref={el => menuRefs.current['doc'] = el}
            style={{ 
              height: (isMobile || !isCollapsed) && openDropdown === 'doc' 
                ? `${menuRefs.current['doc']?.scrollHeight}px` 
                : '0px' 
            }}
          >
            <li className="sub-menu-header1">Document</li>
            <li><Link to="/register" className="sub-link1" onClick={() => isMobile && setMobileOpen(false)}>Create Document</Link></li>
            <li><Link to="/mydoc" className="sub-link1" onClick={() => isMobile && setMobileOpen(false)}>My Document</Link></li>
          </ul>
        </li>

        {/* Nagal Dropdown */}
        <li className={`menu-item1 has-dropdown1 ${openDropdown === 'nagal' ? 'is-open1' : ''}`}>
          <div className="menu-link1" onClick={() => handleDropdown('nagal')}>
            <Layers size={22} className="link-icon1" />
            <span className="link-text1">Nagal</span>
            <ChevronDown size={16} className="arrow-icon1" />
          </div>
          <ul 
            className="sub-menu1"
            ref={el => menuRefs.current['nagal'] = el}
            style={{ 
              height: (isMobile || !isCollapsed) && openDropdown === 'nagal' 
                ? `${menuRefs.current['nagal']?.scrollHeight}px` 
                : '0px' 
            }}
          >
            <li className="sub-menu-header1">Nagal</li>
            <li><Link to="/nl_register" className="sub-link1" onClick={() => isMobile && setMobileOpen(false)}>Create Nagal</Link></li>
            <li><Link to="/mynl" className="sub-link1" onClick={() => isMobile && setMobileOpen(false)}>My Nagal</Link></li>
          </ul>
        </li>

        {/* EC Dropdown */}
        <li className={`menu-item1 has-dropdown1 ${openDropdown === 'ec' ? 'is-open1' : ''}`}>
          <div className="menu-link1" onClick={() => handleDropdown('ec')}>
            <ClipboardList size={22} className="link-icon1" />
            <span className="link-text1">Encumbrance Certificate(EC)</span>
            <ChevronDown size={16} className="arrow-icon1" />
          </div>
          <ul 
            className="sub-menu1"
            ref={el => menuRefs.current['ec'] = el}
            style={{ 
              height: (isMobile || !isCollapsed) && openDropdown === 'ec' 
                ? `${menuRefs.current['ec']?.scrollHeight}px` 
                : '0px' 
            }}
          >
            <li className="sub-menu-header1">Encumbrance Certificate(EC)</li>
            <li><Link to="/ec_register" className="sub-link1" onClick={() => isMobile && setMobileOpen(false)}>Create Encumbrance Certificate(EC)</Link></li>
            <li><Link to="/myec" className="sub-link1" onClick={() => isMobile && setMobileOpen(false)}>My Encumbrance Certificate(EC)</Link></li>
          </ul>
        </li>

        {/* Print Label */}
        <li className="menu-item1">
          <Link
            to="/print-label"
            className={`menu-link1 ${isActive('/print-label')}`}
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <Printer size={22} className="link-icon1" />
            <span className="link-text1">Print Label</span>
          </Link>
        </li>

        {/* Clients */}
        <li className="menu-item1">
          <Link to="/client" className={`menu-link1 ${isActive('/client')}`} onClick={() => isMobile && setMobileOpen(false)}>
            <Users size={22} className="link-icon1" />
            <span className="link-text1">Clients</span>
          </Link>
        </li>

        {/* Admin */}
        {user?.role === 'admin' && (
          <li className="menu-item1">
            <Link to="/admin" className={`menu-link1 admin-link1 ${isActive('/admin')}`} onClick={() => isMobile && setMobileOpen(false)}>
              <ShieldCheck size={22} className="link-icon1" />
              <span className="link-text1">Admin</span>
            </Link>
          </li>
        )}
      </ul>

      {/* Footer Profile */}
      <div className={`sidebar-footer mt-auto border-top p-3 ${(!isMobile && isCollapsed) ? 'collapsed' : ''}`}>
        <div className="d-flex align-items-center justify-content-between gap-2 user-profile-container">
          <div className="user-profile-pill d-flex align-items-center gap-2">
            <div 
  className="user-avatar-circle" 
  style={{
    width: '40px', // Set a fixed width/height for a perfect circle
    height: '40px',
    border: '1px solid yellow', 
    borderRadius: '50%', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    overflow: 'hidden', // Ensures the image stays inside the circle
    backgroundColor: 'transparent'
  }}
>
  {user?.profileImage ? (
    <img 
      src={user.profileImage} 
      alt={user.name} 
      style={{
        width: '100%', 
        height: '100%', 
        objectFit: 'cover' // Prevents the image from stretching
      }} 
    />
  ) : (
    <span style={{ color: 'yellow', fontWeight: 'bold' }}>
      {user?.name?.charAt(0).toUpperCase()}
    </span>
  )}
</div>
            {(!isCollapsed || isMobile) && (
              <div className="d-flex flex-column line-height-1 fade-in">
                <span className="user-name fw-bold small text-truncate text-white">
                  {TitleCase(user?.name)}
                </span>
                <span className="user-role smaller" style={{color:'orange'}}>
                  {TitleCase(user?.role)}
                </span>
              </div>
            )}
          </div>
          <button className="logout-icon-btn btn-hover-danger" onClick={() => logout()} title="Logout">
            <LogOut size={18} color="white" />
          </button>
        </div>
      </div>
    </div>
  );

  // --- MOBILE VIEW ---
  if (isMobile) {
    return (
      <>
        <div className="mobile-header1 d-flex align-items-center p-2" style={{ background: 'rgb(74, 18, 126)', position: 'fixed', right:"20px",  top:"20px", width: '60px', zIndex: 1050, borderRadius:"15px" }}>
          <IconButton onClick={handleMobileToggle} style={{ color: 'white' }}>
            <Menu />
          </IconButton>
        </div>

        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={handleMobileToggle}
          sx={{
    '& .MuiDrawer-paper': {
      width: '280px',
      backgroundColor: '#4a127e', // Your purple
      color: 'white',            // Force text to white
      border: 'none',
      overflowX: 'hidden',
    },
  }}
        >
          <div className="sidebar-header1">
            <div className="logo-section1">
              <span className="logo-text1">Menu</span>
            </div>
            <IconButton onClick={handleMobileToggle} style={{ color: 'white' }}><X /></IconButton>
          </div>
          {renderMenuContent()}
        </Drawer>
      </>
    );
  }

  // --- DESKTOP VIEW ---
  return (
    <aside className={`sidebar-container1 ${isCollapsed ? 'collapsed1' : ''}`} style={{top: marquee.status === 'active' ? "120px": "90px"}}>
      <div className="sidebar-header1">
        <div className="logo-section1">
          {!isCollapsed && <span className="logo-text1">Menu</span>}
        </div>
        <button className="sidebar-toggle1" onClick={toggleSidebar}>
          {isCollapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>
      {renderMenuContent()}
    </aside>
  );
};

export default Sidebar;